import { IApiBaseProps, IPaginatedApiBaseProps, IResultResponse, ISortByQuery } from '@common/api.models';
import { createSearchAsyncFn } from '@common/search.services';
import { envConfigs } from '@config/envConfigs';
import { createDownloadFileAsyncFn, createDownloadFileWithCustomPropsAsyncFn } from '@file/file.services';
import { IFileResponse, IPdfFileDownloadRequest } from '@file/projectFile/projectFile.models';
import { IItemResponse, IListResponse, IPagedResultsV1 } from '@gcl/react-core';
import { apiServerDelete, apiServerGet, apiServerPost, apiServerPut } from '@common/axios.services';
import { IDownloadFileBaseProps } from '@file/file.models';
import {
  IProjectTransmittalHistory,
  IProjectTransmittalHistoryItemResponse,
} from './history/transmittalHistory.models';
import { ETransmittalType } from './transmittal.constants';
import {
  ICreateProjectTransmittalDistributionRoleRequest,
  ICreateProjectTransmittalDistributionTeamRequest,
  ICreateProjectTransmittalDistributionUserRequest,
  ICreateProjectTransmittalRequest,
  IDeleteProjectTransmittalDistributionRequest,
  IDeleteProjectTransmittalRequest,
  IProjectTransmittalBase,
  IProjectTransmittalDetail,
  IProjectTransmittalDistribution,
  IProjectTransmittalDistributionRoleBase,
  IProjectTransmittalDistributionTeamBase,
  IProjectTransmittalDistributionUserBase,
  IProjectTransmittalOption,
  ITransmittalItem,
  ITransmittalSearchFilter,
  IUpdateProjectTransmittalRequest,
} from './transmittal.models';

export const apiRoutes = {
  createDraftTransmittalIfNotExist: `/Transmittals/DraftIfNotExist`,
  getTransmittal: (id: number) => `/Transmittals/${id}`,
  getLatestTransmittal: (masterScheduleId: number) => `/Transmittals/LatestFromMasterSchedule/${masterScheduleId}`,
  updateTransmittal: (id: number) => `/Transmittals/${id}`,
  deleteTransmittal: (id: number) => `/Transmittals/${id}`,
  exportTransmittalDetailExcel: (id: number) => `/Transmittals/${id}/ExportExcel`,
  exportTransmittalDetailPdf: (id: number) => `/Transmittals/${id}/ExportPdf`,
  endorseTransmittal: (id: number) => `/Transmittals/${id}/Endorse`,
  requestTransmittalEndorsement: (id: number) => `/Transmittals/${id}/RequestEndorsement`,
  releaseTransmittalForConstruction: (id: number) => `/Transmittals/${id}/ReleaseForConstruction`,
  searchTransmittal: '/Transmittals/Search',
  exportTransmittalSearchExcel: '/Transmittals/ExportExcel',
  getTransmittalDistribution: (id: number) => `/Transmittals/${id}/Distributions`,
  getTransmittalHistories: '/Transmittals/Histories',
  getTransmittalHistoriesItems: '/Transmittals/Histories/Items',
  exportTransmittalHistoriesExcel: '/Transmittals/Histories/ExportExcel',
  exportTransmittalHistoriesPdf: '/Transmittals/Histories/ExportPdf',
  distributionTeam: '/Transmittals/Distributions/Teams',
  distributionRole: '/Transmittals/Distributions/Roles',
  distributionUser: '/Transmittals/Distributions/Users',
  deleteDistributionTeam: (id: number, distributionTeamId: number) =>
    `/Transmittals/${id}/Distributions/Teams/${distributionTeamId}`,
  deleteDistributionRole: (id: number, distributionRoleId: number) =>
    `/Transmittals/${id}/Distributions/Roles/${distributionRoleId}`,
  deleteDistributionUser: (id: number, distributionId: number) =>
    `/Transmittals/${id}/Distributions/Users/${distributionId}`,
  siteTransmittalsOptionsForChecker: '/Transmittals/SiteTransmittalsForChecker/Options',
  siteTransmittalsOptionsForReviewerer: '/Transmittals/SiteTransmittalsForReviewer/Options',
  downloadTransmittalProjectDiscussionsPdf: (id: number) => `/Transmittals/${id}/ProjectDiscussions/Pdf`,
};

export const searchTransmittalAsync = createSearchAsyncFn<ITransmittalSearchFilter, ITransmittalItem>(
  apiRoutes.searchTransmittal,
);

export interface IExportTransmittalSearchExcelProps extends IDownloadFileBaseProps {
  sortByQuery?: ISortByQuery;
  filter: ITransmittalSearchFilter;
}

export const exportTransmittalSearchExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportTransmittalSearchExcelProps>(apiRoutes.exportTransmittalSearchExcel);

export interface IGetTransmittalHistoriesProps extends IPaginatedApiBaseProps {
  projectMasterScheduleId: number;
  projectTransmittalId: number;
}

export const getTransmittalHistoriesAsync = async (
  apiBaseProps: IGetTransmittalHistoriesProps,
): Promise<IPagedResultsV1<IProjectTransmittalHistory>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalHistory>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getTransmittalHistories}`,
    {
      params: {
        ...apiBaseProps,
      },
    },
  );
  return result.data;
};

export interface IGetTransmittalHistoriesItemsProps extends IApiBaseProps {
  projectMasterScheduleId: number;
  shouldShowRemovedFromUse: boolean;
}

export const getTransmittalHistoriesItemsAsync = async (
  props: IGetTransmittalHistoriesItemsProps,
): Promise<IPagedResultsV1<IProjectTransmittalHistoryItemResponse>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalHistoryItemResponse>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getTransmittalHistoriesItems}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export interface IExportTransmittalHistoriesExcelProps extends IDownloadFileBaseProps {
  projectMasterScheduleId: number;
  transmittalType: string;
  shouldShowRemovedFromUse: boolean;
}

export const exportTransmittalHistoriesExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportTransmittalHistoriesExcelProps>(
    apiRoutes.exportTransmittalHistoriesExcel,
  );

export interface IExportTransmittalHistoriesPdfProps extends IDownloadFileBaseProps {
  projectMasterScheduleId: number;
  shouldIncludeCoverPage: boolean;
}

export const exportTransmittalHistoriesPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportTransmittalHistoriesPdfProps>(
    apiRoutes.exportTransmittalHistoriesPdf,
  );

export const createDraftProjectTransmittalIfNotExistAsync = async (
  request?: ICreateProjectTransmittalRequest,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerPost<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.createDraftTransmittalIfNotExist}`,
    request,
  );
  return result.data;
};

export const getProjectTransmittalByIdAsync = async (
  id: number,
  props?: IApiBaseProps,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerGet<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getTransmittal(id)}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export interface IGetLatestTransmittalByMasterScheduleIdAsyncProps extends IApiBaseProps {
  masterScheduleId?: number;
  transmittalType?: ETransmittalType;
}

export const getLatestProjectTransmittalByMasterScheduleIdAsync = async (
  props: IGetLatestTransmittalByMasterScheduleIdAsyncProps,
): Promise<IItemResponse<IProjectTransmittalBase>> => {
  const result = await apiServerGet<IItemResponse<IProjectTransmittalBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getLatestTransmittal(props.masterScheduleId ?? -1)}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const updateProjectTransmittalAsync = async (
  request?: IUpdateProjectTransmittalRequest,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.updateTransmittal(request?.id || 0)}`,
    request,
  );
  return result.data;
};

export const deleteTransmittalAsync = async (request: IDeleteProjectTransmittalRequest): Promise<IResultResponse> => {
  const { id, ...rest } = request;
  const result = await apiServerDelete<IResultResponse>(`${envConfigs.apiBaseUrl}${apiRoutes.deleteTransmittal(id)}`, {
    params: rest,
  });
  return result.data;
};

export interface IExportTransmittalDetailExcelProps extends IDownloadFileBaseProps {
  projectTransmittalId: number;
  projectMasterScheduleId: number;
  transmittalType: string;
  shouldShowRemovedFromUse: boolean;
}

export const exportTransmittalDetailExcelAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportTransmittalDetailExcelProps>((props) => {
    return apiRoutes.exportTransmittalDetailExcel(props.projectTransmittalId);
  });

export interface IExportTransmittalDetailPdfProps extends IDownloadFileBaseProps {
  projectTransmittalId: number;
}

export const exportTransmittalDetailPdfAsync =
  createDownloadFileWithCustomPropsAsyncFn<IExportTransmittalDetailPdfProps>((props) => {
    return apiRoutes.exportTransmittalDetailPdf(props.projectTransmittalId);
  });

export const endorseProjectTransmittalAsync = async (
  request: IUpdateProjectTransmittalRequest,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.endorseTransmittal(request.id)}`,
    request,
  );
  return result.data;
};

export const requestEndorseProjectTransmittalAsync = async (
  request: IUpdateProjectTransmittalRequest,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.requestTransmittalEndorsement(request.id)}`,
    request,
  );
  return result.data;
};

export const releaseForConstructionProjectTransmittalAsync = async (
  request: IUpdateProjectTransmittalRequest,
): Promise<IItemResponse<IProjectTransmittalDetail>> => {
  const result = await apiServerPut<IItemResponse<IProjectTransmittalDetail>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.releaseTransmittalForConstruction(request.id)}`,
    request,
  );
  return result.data;
};

export const postTransmittalDistributionTeamAsync = async (
  request?: ICreateProjectTransmittalDistributionTeamRequest[],
): Promise<IListResponse<IProjectTransmittalDistributionTeamBase>> => {
  const result = await apiServerPost<IListResponse<IProjectTransmittalDistributionTeamBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.distributionTeam}`,
    request,
  );
  return result.data;
};
export const postTransmittalDistributionRoleAsync = async (
  request?: ICreateProjectTransmittalDistributionRoleRequest[],
): Promise<IListResponse<IProjectTransmittalDistributionRoleBase>> => {
  const result = await apiServerPost<IListResponse<IProjectTransmittalDistributionRoleBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.distributionRole}`,
    request,
  );
  return result.data;
};

export const postTransmittalDistributionUserAsync = async (
  request?: ICreateProjectTransmittalDistributionUserRequest[],
): Promise<IListResponse<IProjectTransmittalDistributionUserBase>> => {
  const result = await apiServerPost<IListResponse<IProjectTransmittalDistributionUserBase>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.distributionUser}`,
    request,
  );
  return result.data;
};

export const deleteTransmittalDistributionTeamAsync = async (
  request: IDeleteProjectTransmittalDistributionRequest,
): Promise<IResultResponse> => {
  const { id, ...rest } = request;
  const result = await apiServerDelete<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.deleteDistributionTeam(request.transmittalId || 0, id)}`,
    { params: rest },
  );
  return result.data;
};

export const deleteTransmittalDistributionRoleAsync = async (
  request: IDeleteProjectTransmittalDistributionRequest,
): Promise<IResultResponse> => {
  const { id, ...rest } = request;
  const result = await apiServerDelete<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.deleteDistributionRole(request.transmittalId || 0, id)}`,
    { params: rest },
  );
  return result.data;
};

export const deleteTransmittalDistributionUserAsync = async (
  request: IDeleteProjectTransmittalDistributionRequest,
): Promise<IResultResponse> => {
  const { id, ...rest } = request;
  const result = await apiServerDelete<IResultResponse>(
    `${envConfigs.apiBaseUrl}${apiRoutes.deleteDistributionUser(request.transmittalId || 0, id)}`,
    { params: rest },
  );
  return result.data;
};

export const getProjectTransmittalDistributionsAsync = async (
  id: number,
  props?: IApiBaseProps,
): Promise<IItemResponse<IProjectTransmittalDistribution>> => {
  const result = await apiServerGet<IItemResponse<IProjectTransmittalDistribution>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.getTransmittalDistribution(id)}`,
    {
      params: {
        ...props,
      },
    },
  );
  return result.data;
};

export const getProjectTransmittalSiteTransmittalForCheckerOptionsAsync = async (
  props?: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectTransmittalOption>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalOption>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.siteTransmittalsOptionsForChecker}`,
    {
      params: props,
    },
  );
  return result.data;
};

export const getProjectTransmittalSiteTransmittalForReviewerOptionsAsync = async (
  props?: IApiBaseProps,
): Promise<IPagedResultsV1<IProjectTransmittalOption>> => {
  const result = await apiServerGet<IPagedResultsV1<IProjectTransmittalOption>>(
    `${envConfigs.apiBaseUrl}${apiRoutes.siteTransmittalsOptionsForReviewerer}`,
    {
      params: props,
    },
  );
  return result.data;
};

export const downloadTransmittalProjectDiscussionsPdfAsync = async ({
  id,
  projectId,
  onDownloadProgressUpdate,
}: IPdfFileDownloadRequest): Promise<IFileResponse> => {
  return createDownloadFileAsyncFn(apiRoutes.downloadTransmittalProjectDiscussionsPdf(id), {
    id,
    projectId,
    onDownloadProgressUpdate,
  });
};
